/**
 * CorrespondentProfileTab — the "Company / identity" block, editable.
 *
 * Laid out with the SAME primitives as the DSCR pricing screen —
 * `PricingPageLayout` (content column + sticky anchor rail on the right),
 * `PricingSection` (the collapsible titled cards) and `AdminField` (label above
 * the control) — so the two pages read as one system rather than two takes on a
 * form. See ADMIN_UI.md.
 *
 * Everything here saves through `POST /correspondents/{companyId}/update`,
 * which `$set`s only the keys you send. That's the whole group the FE doc lists
 * under "Company / identity": name, relationship, address, phone, notes,
 * website, Box folders and the MLPA stamps. The logo URL, email domains,
 * onboarding volumes, bank details and `appraisalLenderNames` live on the
 * **Admin** tab (CorrespondentAdminTab.js), which saves through the same
 * endpoint; each tab sends only its own keys.
 *
 * This tab used to be read-only `FieldRow`s. Nothing on it could be corrected
 * from the UI, including `correspondentRelationship`.
 *
 * NOT here, on purpose:
 * - `buyRate` / `tfFee` / margins / spreads → the **Pricing** tab. They go
 *   through `POST /{companyId}/pricing/update`, which writes `pricingAuditTrail`;
 *   routing them through the plain update would lose the audit entry.
 * - `standardMortgageeClauses` → superseded by the structured `fundingEntities`
 *   (whose computed `mortgageeClause` is what validates a loan's mortgagee).
 *   The model keeps it for back-compat only, so it gets no editor.
 * - `pricingAuditTrail`, `documents`, `editorNote`, `servicing`,
 *   `guidelineOverlays`, contacts → owned by their own tabs.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import CheckRounded from "@mui/icons-material/CheckRounded";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import AddRounded from "@mui/icons-material/AddRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import SearchOutlined from "@mui/icons-material/SearchOutlined";

import Flx from "../../../components/layout/Flx";
import { selectOptionsUsaStates } from "../../../constants/selectOptions/selectOptionsUsaStates";
import {
  fetchPlaceDetails,
  mapPlaceDetailsToAddress,
  searchPlaces,
} from "../../../utils/geocode/googlePlaces";
import { formatPhone } from "../../../_src_shared/utils/functions/formatPhone";
import { slate, teal, status as statusColors } from "../../../theme/colors";
import {
  PricingPageLayout,
  // Profile inputs use the field label / helper standard (FIELD_TEXT), the
  // same as the Pricing tab — hence the alias.
  PricingFieldText as PricingField,
  T as PT,
} from "../../admin/AdminPricingScreen/pricingUi";
import { FIELD_TEXT } from "../../../theme/formStyles";
import { segmentedGroupSx } from "../../../theme/activeStates";
import {
  ONBOARDING_STATUSES,
  useCorrespondentsApi,
} from "../../../hooks/useCorrespondentsHook";
import AgreementDocumentModal from "./AgreementDocumentModal";
import {
  ONBOARDING_TONE,
  SoftBadge,
  StatusChip,
} from "./correspondentUi";
import PendingChangesBar from "../../../components/common/PendingChangesBar";
import {
  Cell,
  ColumnHead,
  CorrespondentCheckbox,
  FIELD_SX,
  Grid,
  ProfileSection,
} from "./correspondentForm";
import { FileSlot, useCorrespondentUpload } from "./CorrespondentFileSlot";
import { CorrespondentContactPanel } from "./CorrespondentPanelForms";
import { oidOf } from "../../../hooks/useCorrespondentsHook";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";

// Anchor rail — mirrors the section ids below, same as `dscrRail` does on the
// pricing screen.
const PROFILE_RAIL = [
  { id: "profile-status", label: "Relationship status" },
  { id: "profile-company", label: "Company profile" },
  { id: "profile-address", label: "Address" },
  { id: "profile-box", label: "Box folders" },
  { id: "profile-mlpa", label: "Legal Agreements" },
];

// The agreements the Legal Agreements check boxes set — the onboarding
// checklist's `agreementTypes` (ONBOARDING_AGREEMENT_TYPE_OPTIONS), the same
// list the Onboarding Checklist's Compliance section edits.
const AGREEMENT_MLPA = "MLPA";
const OTHER_AGREEMENTS = ["LFA", "Broker Agreement"];

// The two global correspondent roots, verbatim from BOX_CORRESPONDENT_ROOTS in
// the backend (app/models/box_to_los_sync.py). A correspondent's folders sit
// DIRECTLY under one of these, which is why boxFolders is a list — the same
// company can have a folder in the current tree and another in the legacy one.
const BOX_CORRESPONDENT_ROOTS = [
  { id: "305783007987", name: "Eastview Correspondents 2025" },
  { id: "158739101177", name: "TIER AA" },
];

// Mirrors box_folder_url() in the backend model. The model's _fill_url validator
// derives this from folderId, but POST /{id}/update is a raw $set that never
// constructs the pydantic model — so nothing derives it server-side on this
// path and we have to send the composed value ourselves.
const boxFolderUrl = (folderId) => {
  const fid = String(folderId ?? "").trim();
  return fid ? `https://app.box.com/folder/${fid}` : null;
};

// People copy the folder's address bar, not its id. Accept either.
const parseBoxFolderId = (v) => {
  const raw = String(v ?? "").trim();
  const m = raw.match(/\/folder\/(\d+)/);
  if (m) return m[1];
  return raw.replace(/[^\d]/g, "");
};

// The MLPA family on CorrespondentDocument.category. The legacy short form is
// still accepted by the backend (MLPA_CATEGORY_ALIASES), so match both.
const MLPA_CATEGORIES = ["Mortgage Loan Purchase Agreement", "MLPA"];

/**
 * Google Places search that FILLS the address parts below it rather than
 * replacing them — the manual fields stay the editable source of truth.
 * Uses the same Places search + place → address mapping as every other
 * address input (utils/geocode/googlePlaces), so the parts land in the
 * backend's `Address` shape. `onPick` gets the mapped address object.
 */
function AddressSearch({ onPick }) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runSearch = useMemo(
    () =>
      debounce(async (q) => {
        if (!q || q.length < 3) {
          setOptions([]);
          setLoading(false);
          return;
        }
        setLoading(true);
        const results = await searchPlaces(q);
        setOptions(results);
        setLoading(false);
      }, 300),
    [],
  );
  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const pick = async (option) => {
    if (!option?.placeId) return;
    setError(null);
    setLoading(true);
    try {
      const place = await fetchPlaceDetails(option.placeId);
      onPick(mapPlaceDetailsToAddress(place, option.placeId));
      setInputValue("");
      setOptions([]);
    } catch {
      setError("Couldn't load that address. Try again or type it in below.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Autocomplete
        freeSolo
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={(o) => (typeof o === "string" ? o : o.fullAddress || "")}
        inputValue={inputValue}
        value={null}
        onInputChange={(e, v, reason) => {
          if (reason !== "input" && reason !== "clear") return;
          setInputValue(v);
          runSearch(v);
        }}
        onChange={(e, v) => {
          if (v && typeof v === "object") pick(v);
        }}
        noOptionsText={
          inputValue.length < 3 ? "Keep typing…" : "No matching addresses"
        }
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.id}>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 13, color: PT.ink }}>
                {o.mainText || o.fullAddress}
              </Box>
              {o.secondaryText && (
                <Box sx={{ fontSize: 12, color: PT.muted }}>
                  {o.secondaryText}
                </Box>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Start typing an address"
            sx={FIELD_SX}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: PT.faint }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error && (
        <Box sx={{ ...FIELD_TEXT.helper, color: statusColors.error.fg }}>
          {error}
        </Box>
      )}
    </>
  );
}

/** Copy-to-clipboard for a Box link. Confirms in place — this tab has no snackbar. */
function CopyLinkButton({ url }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard denied (insecure origin / permission) — the link text is
      // selectable, so there's still a way through.
    }
  };

  return (
    <Tooltip title={copied ? "Copied" : "Copy link"}>
      <IconButton
        size="small"
        aria-label="Copy Box folder link"
        onClick={copy}
        sx={{ flexShrink: 0 }}
      >
        {copied ? (
          <CheckRounded sx={{ fontSize: 16, color: statusColors.success.fg }} />
        ) : (
          <ContentCopyRounded sx={{ fontSize: 15, color: PT.faint }} />
        )}
      </IconButton>
    </Tooltip>
  );
}

/**
 * One `boxFolders[]` entry — the correspondent's own Box folder.
 *
 * Distinct from the per-LOAN box_correspondent_folder_id/_url on a deal: these
 * are the COMPANY-level reference folders, and they are what
 * `find_loan_box_folder` searches within to match a request to its loan folder
 * (it walks the correspondent's own trees instead of the global roots), so an
 * empty list is why that discovery finds nothing.
 *
 * The card is PROGRESSIVE. A freshly added row has nothing to name or classify
 * yet, so it renders as one paste target and nothing else; the link read-out and
 * the rest of the fields appear once an id is in hand. That keeps "Add row" a
 * single button instead of a separate paste box beside it, and puts the paste
 * where the row is going to live.
 */
function BoxFolderRow({ link, index, onChange, onRemove, duplicate }) {
  const set = (k, v) => onChange(index, { [k]: v });
  const url = boxFolderUrl(link.folderId);

  const removeButton = (
    <Tooltip title="Remove this folder">
      <IconButton
        size="small"
        aria-label="Remove Box folder"
        onClick={() => onRemove(index)}
        sx={{ flexShrink: 0 }}
      >
        <DeleteOutlineRounded sx={{ fontSize: 17, color: PT.faint }} />
      </IconButton>
    </Tooltip>
  );

  // ── empty row: just the paste target ────────────────────────────────────
  if (!link.folderId) {
    return (
      <Box
        sx={{
          border: `1px dashed ${PT.lineStrong}`,
          borderRadius: "8px",
          p: 1.75,
          background: PT.surface2,
        }}
      >
        <Flx g={1} sx={{ alignItems: "flex-start", minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <PricingField
              label="Box folder link"
              helperText="Paste the folder's app.box.com link (or just its id) — the id is read out of the URL, and the rest of the fields appear once it is."
            >
              <TextField
                size="small"
                fullWidth
                autoFocus
                value={link.folderIdInput ?? ""}
                onChange={(e) =>
                  onChange(index, {
                    folderIdInput: e.target.value,
                    folderId: parseBoxFolderId(e.target.value),
                  })
                }
                placeholder="https://app.box.com/folder/305783007987"
                sx={FIELD_SX}
              />
            </PricingField>
          </Box>
          <Box sx={{ pt: 3.25 }}>{removeButton}</Box>
        </Flx>
      </Box>
    );
  }

  // ── resolved row: link read-out + the full form ─────────────────────────
  return (
    <Box
      sx={{
        border: `1px solid ${PT.line}`,
        borderRadius: "8px",
        p: 1.75,
        background: "#fff",
      }}
    >
      <Flx ac jb g={1} sx={{ mb: 1.5, minWidth: 0 }}>
        <Flx ac g={1} sx={{ minWidth: 0 }}>
          <FolderOutlined
            sx={{ fontSize: 16, color: PT.faint, flexShrink: 0 }}
          />
          <Box
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "12.5px",
              fontWeight: 600,
              color: PT.accent,
              textDecoration: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {link.folderName || url}
            <OpenInNewRounded
              sx={{ fontSize: 12, ml: 0.5, verticalAlign: "-1px" }}
            />
          </Box>
        </Flx>
        {removeButton}
      </Flx>

      {/* The full derived link, shown the way the funding-entities editor shows
          its computed mortgageeClause: a tinted monospace read-out that wraps
          instead of truncating, so the whole URL is readable and selectable
          even when the header link is labelled with the folder name. */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            mb: 0.625,
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: PT.faint,
          }}
        >
          Folder URL
        </Box>
        <Flx
          g={0.5}
          sx={{
            alignItems: "flex-start",
            background: teal[50],
            border: `1px solid ${teal[200]}`,
            borderRadius: "6px",
            pl: 1.5,
            pr: 0.5,
            py: 0.5,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              py: 0.5,
              fontSize: "12.5px",
              lineHeight: 1.6,
              color: teal[800],
              overflowWrap: "anywhere",
              userSelect: "all",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            }}
          >
            {url}
          </Box>
          <Flx ac g={0.25} sx={{ flexShrink: 0 }}>
            <Tooltip title="Open in a new tab">
              <IconButton
                size="small"
                aria-label="Open Box folder in a new tab"
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <OpenInNewRounded sx={{ fontSize: 15, color: teal[700] }} />
              </IconButton>
            </Tooltip>
            <CopyLinkButton url={url} />
          </Flx>
        </Flx>
      </Box>

      {duplicate && (
        <Box
          sx={{ mb: 2, fontSize: 12.5, color: statusColors.error.fg }}
        >
          Folder {link.folderId} is on this correspondent more than once.
        </Box>
      )}

      <Grid>
        <Cell span={4} spanSm={6}>
          <PricingField
            label="Folder id"
            helperText="Paste the id or the app.box.com link."
          >
            <TextField
              size="small"
              fullWidth
              value={link.folderId || ""}
              onChange={(e) =>
                onChange(index, {
                  folderIdInput: e.target.value,
                  folderId: parseBoxFolderId(e.target.value),
                })
              }
              placeholder="305783007987"
              sx={FIELD_SX}
            />
          </PricingField>
        </Cell>
        <Cell span={4} spanSm={6}>
          <PricingField label="Folder name">
            <TextField
              size="small"
              fullWidth
              value={link.folderName || ""}
              onChange={(e) => set("folderName", e.target.value)}
              placeholder="As it appears in Box"
              sx={FIELD_SX}
            />
          </PricingField>
        </Cell>
        <Cell span={4} spanSm={12}>
          <PricingField label="Parent tree">
            <TextField
              select
              size="small"
              fullWidth
              value={link.parentFolderId || ""}
              onChange={(e) => {
                const root = BOX_CORRESPONDENT_ROOTS.find(
                  (r) => r.id === e.target.value,
                );
                onChange(index, {
                  parentFolderId: root?.id || "",
                  parentFolderName: root?.name || "",
                });
              }}
              sx={FIELD_SX}
            >
              <MenuItem value="" sx={{ fontSize: 13, color: PT.faint }}>
                — Not set —
              </MenuItem>
              {BOX_CORRESPONDENT_ROOTS.map((r) => (
                <MenuItem key={r.id} value={r.id} sx={{ fontSize: 13 }}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
          </PricingField>
        </Cell>
        <Cell span={12}>
          <PricingField label="Notes">
            <TextField
              size="small"
              fullWidth
              value={link.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              sx={FIELD_SX}
            />
          </PricingField>
        </Cell>
      </Grid>
    </Box>
  );
}

// `Address.fullAddress` is a stored string, not a computed field — if the parts
// are edited and it isn't, the header and every list that reads it go stale.
// Composed from the parts so the two can't disagree.
const composeFullAddress = (a) => {
  const street = [a?.streetNumber, a?.streetName].filter(Boolean).join(" ");
  const loc = [a?.city, [a?.state, a?.zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [street, loc].filter(Boolean).join(", ");
};

// Phone follows the same contract as RffPhoneField: RAW DIGITS in state,
// `formatPhone` for display. `companyPhone` is typed `str | int` on the model,
// so coerce before stripping. Capped at 10 so extra typed digits can't hide
// behind the mask (formatPhone only renders the first 10).
const phoneDigits = (v) =>
  String(v ?? "")
    .replace(/[^d]/g, "")
    .slice(0, 10);

// Toggle value for "no status" — saved as null (see save()).
const NO_STATUS = "__none__";

// `<input type="date">` wants yyyy-MM-dd; the model stores a datetime.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");

export default function CorrespondentProfileTab({
  detail,
  patch,
  onChanged,
  onOpenAgreements,
}) {
  const buildDraft = useCallback(
    () => ({
      onboardingStatus: detail.onboardingStatus ?? "",
      inactiveReason: detail.inactiveReason ?? "",
      companyName: detail.companyName ?? "",
      correspondentRelationship: detail.correspondentRelationship ?? "",
      companyPhone: phoneDigits(detail.companyPhone),
      companyWebsite: detail.companyWebsite ?? "",
      companyNotes: detail.companyNotes ?? "",
      address: {
        streetNumber: detail.companyAddress?.streetNumber ?? "",
        streetName: detail.companyAddress?.streetName ?? "",
        city: detail.companyAddress?.city ?? "",
        state: detail.companyAddress?.state ?? "",
        zip: detail.companyAddress?.zip ?? "",
        county: detail.companyAddress?.county ?? "",
      },
      boxLinks: (detail.boxFolders ?? []).filter(Boolean).map((l, i) => ({
        _key: String(l.oid ?? l._id ?? `saved-${i}`),
        ...(l.oid ? { oid: l.oid } : {}),
        ...(l._id ? { _id: l._id } : {}),
        folderId: l.folderId ?? "",
        folderName: l.folderName ?? "",
        parentFolderId: l.parentFolderId ?? "",
        parentFolderName: l.parentFolderName ?? "",
        notes: l.notes ?? "",
      })),
      mlpaSigned: !!detail.mlpaSigned,
      mlpaSignedDate: toDateInput(detail.mlpaSignedDate),
      mlpaSignatoryTitle: detail.mlpaSignatoryTitle ?? "",
    }),
    [detail],
  );

  const [draft, setDraft] = useState(buildDraft);
  const [saving, setSaving] = useState(false);
  // Only shout about the missing reason once they've tried to save (or typed).
  const [reasonTouched, setReasonTouched] = useState(false);
  // The MLPA version whose preview modal is open (null = closed).
  const [previewDoc, setPreviewDoc] = useState(null);
  // Monotonic id for unsaved boxFolders rows. A derived key (index / length)
  // collides once a row in the middle is removed and another added.
  const newBoxLinkKey = useRef(0);

  // Legal Agreements: which agreements are in place lives on the onboarding
  // checklist (agreementTypes), not on the correspondent record, so it's read
  // and saved through the checklist — one value shared with the Onboarding
  // Checklist's Compliance section. null until loaded.
  const correspondentsApi = useCorrespondentsApi();
  const cid = detail?._id ?? detail?.oid;
  const [savedAgreements, setSavedAgreements] = useState(null);
  const [agreements, setAgreements] = useState([]);
  useEffect(() => {
    if (!cid) return undefined;
    let alive = true;
    correspondentsApi
      .getOnboardingChecklist(cid)
      .then((v) => {
        if (!alive) return;
        const list = v?.checklist?.agreementTypes ?? [];
        setSavedAgreements(list);
        setAgreements(list);
      })
      .catch(() => alive && setSavedAgreements(null));
    return () => {
      alive = false;
    };
  }, [cid, correspondentsApi, detail]);
  const toggleAgreement = (name, on) =>
    setAgreements((list) =>
      on ? [...new Set([...list, name])] : list.filter((a) => a !== name),
    );
  const agreementsDirty =
    savedAgreements !== null &&
    JSON.stringify([...agreements].sort()) !==
      JSON.stringify([...savedAgreements].sort());

  const baseline = useMemo(() => buildDraft(), [buildDraft]);
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(baseline) || agreementsDirty,
    [draft, baseline, agreementsDirty],
  );

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  // Editing a part by hand after a search pick drops the pick's coordinates —
  // they'd no longer describe the address being saved.
  const setAddr = (k, v) =>
    setDraft((d) => ({
      ...d,
      address: {
        ...d.address,
        [k]: v,
        ...(k !== "county" && "latitude" in d.address
          ? { latitude: null, longitude: null }
          : {}),
      },
    }));
  // A search pick fills every part at once (plus country + coordinates, which
  // ride along to the saved address but have no field of their own here).
  const fillAddress = (a) =>
    setDraft((d) => ({
      ...d,
      address: {
        ...d.address,
        streetNumber: a.streetNumber || "",
        streetName: a.streetName || "",
        city: a.city || "",
        state: a.state || "",
        zip: a.zip || "",
        county: a.county || "",
        country: a.country || null,
        latitude: a.latitude || null,
        longitude: a.longitude || null,
      },
    }));
  const setBoxLink = (index, patch) =>
    setDraft((d) => ({
      ...d,
      boxLinks: d.boxLinks.map((l, i) => (i === index ? { ...l, ...patch } : l)),
    }));
  const removeBoxLink = (index) =>
    setDraft((d) => ({
      ...d,
      boxLinks: d.boxLinks.filter((_, i) => i !== index),
    }));
  const addBoxLink = () =>
    setDraft((d) => ({
      ...d,
      boxLinks: [
        ...d.boxLinks,
        {
          // Only new rows carry a "new-" key; `oid` is deliberately omitted —
          // the update endpoint's raw $set never mints one, and nothing that
          // reads boxFolders (find_loan_box_folder) needs it.
          _key: `new-${(newBoxLinkKey.current += 1)}`,
          folderIdInput: "",
          folderId: "",
          folderName: "",
          parentFolderId: "",
          parentFolderName: "",
          notes: "",
        },
      ],
    }));

  const isInactive = draft.onboardingStatus === "Inactive";
  // A reason is REQUIRED to move a correspondent to Inactive. The backend
  // accepts the write either way, so the rule lives here; PendingChangesBar has
  // no disabled state, so save() refuses and the field shows why.
  const missingReason = isInactive && !draft.inactiveReason.trim();

  const save = async () => {
    if (missingReason) {
      setReasonTouched(true);
      return;
    }
    setSaving(true);
    try {
      // The agreement list first — the record patch below refreshes the
      // screen, which re-reads it (and the onboarding progress).
      if (agreementsDirty) {
        await correspondentsApi.saveOnboardingChecklist(cid, {
          agreementTypes: agreements,
        });
      }
      const address = {
        ...(detail.companyAddress || {}),
        ...draft.address,
        fullAddress: composeFullAddress(draft.address),
      };
      await patch({
        onboardingStatus: draft.onboardingStatus || null,
        // Clear the reason when the partner isn't Inactive, so a stale "why we
        // stopped" can't sit on a reactivated correspondent.
        inactiveReason: isInactive ? draft.inactiveReason.trim() : null,
        companyName: draft.companyName,
        correspondentRelationship: draft.correspondentRelationship,
        companyPhone: draft.companyPhone,
        companyWebsite: draft.companyWebsite,
        companyNotes: draft.companyNotes,
        companyAddress: address,
        // Drop the client key and any half-typed row, and compose `url`
        // ourselves — the model validator that derives it never runs on this
        // endpoint (see boxFolderUrl above).
        boxFolders: draft.boxLinks
          .filter((l) => String(l.folderId || "").trim())
          .map(({ _key, folderIdInput, ...l }) => ({
            ...l,
            url: boxFolderUrl(l.folderId),
          })),
        mlpaSigned: draft.mlpaSigned,
        // Empty date clears the stamp rather than posting "".
        mlpaSignedDate: draft.mlpaSignedDate || null,
        mlpaSignatoryTitle: draft.mlpaSignatoryTitle,
      });
    } finally {
      setSaving(false);
    }
  };

  const previewAddress = composeFullAddress(draft.address);

  // The MLPA lineage off the correspondent's own `documents` array — the
  // agreement of record if one has been promoted, else the newest turn. It is
  // already on `detail`, so showing it here costs no extra fetch.
  const mlpaVersions = useMemo(
    () =>
      (detail?.documents ?? []).filter(
        (d) => d && MLPA_CATEGORIES.includes(d.category),
      ),
    [detail],
  );
  const mlpaDoc = useMemo(() => {
    if (!mlpaVersions.length) return null;
    return (
      mlpaVersions.find((d) => d.isCurrentAgreement) ||
      [...mlpaVersions].sort(
        (a, b) => (b.agreementVersion || 0) - (a.agreementVersion || 0),
      )[0]
    );
  }, [mlpaVersions]);

  // The MLPA signatory (mlpaSignedBy) — a contact record saved straight
  // away, like the Contacts tab does. Its title is kept on the company too
  // (mlpaSignatoryTitle), so mirror it into the draft or the profile would
  // read as changed and a later save could put the old title back.
  const [editingSignatory, setEditingSignatory] = useState(false);
  const signatory = detail?.mlpaSignedBy || null;
  const signatoryLabel = signatory
    ? [
        [signatory.firstName, signatory.lastName].filter(Boolean).join(" ") ||
          signatory.fullName,
        signatory.emailAddress,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";
  const saveSignatory = async (payload) => {
    const title = payload?.title || "";
    await patch({ mlpaSignedBy: payload, mlpaSignatoryTitle: title });
    setDraft((d) => ({ ...d, mlpaSignatoryTitle: title }));
    setEditingSignatory(false);
  };

  // Executed MLPA upload — straight from the MLPA card, same flow as the
  // onboarding questionnaire.
  const mlpaUpload = useCorrespondentUpload(detail?._id ?? detail?.oid);
  const uploadMlpa = async (file) => {
    const res = await mlpaUpload.upload(file, {
      category: "Mortgage Loan Purchase Agreement",
      agreementStage: "Final Executed",
      ...(mlpaDoc ? { supersedesDocumentId: oidOf(mlpaDoc) } : {}),
    });
    if (!res) return;
    // An executed agreement on file means it's signed — tick it (and stamp
    // today if no signed date yet); saved with the rest of the profile.
    setDraft((d) => ({
      ...d,
      mlpaSigned: true,
      mlpaSignedDate:
        d.mlpaSignedDate || formatDateTime(new Date().toISOString(), "yyyy-MM-dd"),
    }));
    onChanged?.();
  };

  return (
    <>
      <PricingPageLayout nav={PROFILE_RAIL}>
        <ProfileSection
          id="profile-status"
          title="Relationship status"
          desc="Where this partner stands with Eastview. Onboarding = agreement and setup in progress, Active = selling to EV, Inactive = no longer selling."
        >
          <Grid>
            <Cell span={12}>
              <PricingField label="Status">
                {/* A segmented toggle rather than a select: four options, and
                    the selected one wears its status color, so it doubles as
                    the "currently" read-out. "No status" saves as null. */}
                <ToggleButtonGroup
                  exclusive
                  value={draft.onboardingStatus || NO_STATUS}
                  onChange={(_, v) => {
                    // Clicking the selected option again reports null — ignore.
                    if (v == null) return;
                    set("onboardingStatus", v === NO_STATUS ? "" : v);
                  }}
                  aria-label="Relationship status"
                  sx={{
                    ...segmentedGroupSx({ fontSize: 13 }),
                    alignSelf: "flex-start",
                    flexWrap: "wrap",
                    border: `1px solid ${PT.line}`,
                  }}
                >
                  {[NO_STATUS, ...ONBOARDING_STATUSES].map((o) => {
                    const tone = ONBOARDING_TONE[o] || statusColors.neutral;
                    return (
                      <ToggleButton
                        key={o}
                        value={o}
                        sx={{
                          "&.Mui-selected, &.Mui-selected:hover": {
                            backgroundColor: tone.bg,
                            color: tone.fg,
                            fontWeight: 700,
                          },
                        }}
                      >
                        {o === NO_STATUS ? "No status" : o}
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
              </PricingField>
            </Cell>

            {isInactive && (
              <Cell span={12}>
                <PricingField
                  label="Reason for going inactive"
                  required
                  helperText="Why this partner stopped selling to EV — e.g. 'Not selling to EV', 'Relationship ended'."
                >
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={draft.inactiveReason}
                    onChange={(e) => {
                      setReasonTouched(true);
                      set("inactiveReason", e.target.value);
                    }}
                    error={reasonTouched && missingReason}
                    sx={FIELD_SX}
                  />
                </PricingField>
                {reasonTouched && missingReason && (
                  <Box sx={{ ...FIELD_TEXT.helper, color: statusColors.error.fg }}>
                    A reason is required to mark this partner Inactive.
                  </Box>
                )}
              </Cell>
            )}
          </Grid>
        </ProfileSection>

        <ProfileSection
          id="profile-company"
          title="Company profile"
          desc="Identity and contact details for the partner company."
        >
            <Grid>
              <Cell span={5} spanSm={12}>
                <PricingField label="Company name">
                  <TextField
                    size="small"
                    fullWidth
                    value={draft.companyName}
                    onChange={(e) => set("companyName", e.target.value)}
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>
              <Cell span={4} spanSm={6}>
                <PricingField label="Relationship">
                  <TextField
                    size="small"
                    fullWidth
                    value={draft.correspondentRelationship}
                    onChange={(e) =>
                      set("correspondentRelationship", e.target.value)
                    }
                    placeholder="e.g. Correspondent"
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>

              <Cell span={3} spanSm={6}>
                <PricingField label="Phone">
                  <TextField
                    size="small"
                    fullWidth
                    value={formatPhone(draft.companyPhone)}
                    onChange={(e) =>
                      set("companyPhone", phoneDigits(e.target.value))
                    }
                    placeholder="(555) 123-4567"
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>
              <Cell span={12}>
                <PricingField label="Website">
                  <TextField
                    size="small"
                    fullWidth
                    value={draft.companyWebsite}
                    onChange={(e) => set("companyWebsite", e.target.value)}
                    placeholder="https://…"
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>

              <Cell span={12}>
                <PricingField label="Notes">
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={draft.companyNotes}
                    onChange={(e) => set("companyNotes", e.target.value)}
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>
            </Grid>
        </ProfileSection>

        <ProfileSection
          id="profile-address"
          title="Address"
          desc="The company's mailing address. Search to fill it in, or type the parts yourself."
        >
            <Grid>
              <Cell span={12}>
                <PricingField
                  label="Find an address"
                  helperText="Pick a match to fill in the fields below. You can still change any of them."
                >
                  <AddressSearch onPick={fillAddress} />
                </PricingField>
              </Cell>
              <Cell span={3} spanSm={4}>
                <PricingField label="Street number">
                  <TextField
                    size="small"
                    fullWidth
                    value={draft.address.streetNumber}
                    onChange={(e) => setAddr("streetNumber", e.target.value)}
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>
              <Cell span={9} spanSm={8}>
                <PricingField label="Street name">
                  <TextField
                    size="small"
                    fullWidth
                    value={draft.address.streetName}
                    onChange={(e) => setAddr("streetName", e.target.value)}
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>

              <Cell span={4} spanSm={6}>
                <PricingField label="City">
                  <TextField
                    size="small"
                    fullWidth
                    value={draft.address.city}
                    onChange={(e) => setAddr("city", e.target.value)}
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>
              <Cell span={2} spanSm={6}>
                <PricingField label="State">
                  {/* Initials only, same control as the funding-entity address. */}
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={draft.address.state}
                    onChange={(e) => setAddr("state", e.target.value)}
                    sx={FIELD_SX}
                    SelectProps={{
                      MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 13, color: PT.faint }}>
                      —
                    </MenuItem>
                    {selectOptionsUsaStates.map((o) => (
                      <MenuItem
                        key={o.value}
                        value={o.value}
                        sx={{ fontSize: 13 }}
                      >
                        {o.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </PricingField>
              </Cell>
              <Cell span={3} spanSm={6}>
                <PricingField label="ZIP">
                  <TextField
                    size="small"
                    fullWidth
                    value={draft.address.zip}
                    onChange={(e) => setAddr("zip", e.target.value)}
                    inputProps={{ maxLength: 10 }}
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>
              <Cell span={3} spanSm={6}>
                <PricingField label="County">
                  <TextField
                    size="small"
                    fullWidth
                    value={draft.address.county}
                    onChange={(e) => setAddr("county", e.target.value)}
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>

              <Cell span={12}>
                <Box
                  sx={{
                    background: PT.accentSoft,
                    border: `1px solid ${PT.line}`,
                    borderRadius: "6px",
                    px: 1.5,
                    py: 1.25,
                    fontSize: "12.5px",
                    color: PT.accentInk,
                  }}
                >
                  <b>Full address</b> · {previewAddress || "—"}
                  <Box sx={{ mt: 0.5, fontSize: "11.5px", color: PT.muted }}>
                    Composed from the parts above and written on save —
                    `fullAddress` is a stored string, not computed, so it would
                    otherwise go stale wherever it&apos;s displayed.
                  </Box>
                </Box>
              </Cell>
            </Grid>
        </ProfileSection>

        <ProfileSection
          id="profile-box"
          title={`Box folders (${draft.boxLinks.length})`}
          desc="The correspondent's own top-level Box folders, which loan-folder discovery searches within."
          actions={
            <Button
              variant="outlined"
              startIcon={<AddRounded sx={{ fontSize: 17 }} />}
              onClick={addBoxLink}
            >
              Add row
            </Button>
          }
        >
          <Box
            sx={{
              mb: 2,
              fontSize: 12.5,
              lineHeight: 1.6,
              color: PT.muted,
              background: PT.surface2,
              border: `1px solid ${PT.line}`,
              borderRadius: "6px",
              px: 1.5,
              py: 1.25,
            }}
          >
            A company can have one folder in the current
            Correspondents 2025 tree and another in the legacy TIER AA tree, so
            this is a list. It&apos;s normally kept up to date by the opening
            team&apos;s Box folder refresh; edit it here to correct or add what
            that missed. These are the company&apos;s folders — a loan&apos;s
            own folder link lives on the loan.
          </Box>
          {draft.boxLinks.length > 0 && (
            <Flx column g={1.5}>
              {draft.boxLinks.map((link, i) => (
                <BoxFolderRow
                  key={link._key}
                  link={link}
                  index={i}
                  onChange={setBoxLink}
                  onRemove={removeBoxLink}
                  duplicate={
                    !!link.folderId &&
                    draft.boxLinks.some(
                      (o, j) => j !== i && o.folderId === link.folderId,
                    )
                  }
                />
              ))}
            </Flx>
          )}
          {draft.boxLinks.length === 0 && (
            <Box sx={{ fontSize: 12.5, color: PT.faint }}>
              No Box folders recorded yet. Until one is added, no loan folders
              can be found for this correspondent.
            </Box>
          )}
        </ProfileSection>

        <ProfileSection
          id="profile-mlpa"
          title="Legal Agreements"
          desc="Tick each agreement this partner has. These are the same agreements chosen in the Onboarding Checklist's Compliance section, so set them in either place. Loans can't be purchased until the Master Loan Purchase Agreement is executed; promoting a version to Final Executed on the Agreements tab fills in the MLPA signed status and date automatically."
        >
          {/* MLPA / LFA / Broker Agreement side by side. MLPA signed is the
              record's own flag; ticking it also adds MLPA to the agreements. */}
          <Flx wrap g={3} sx={{ mb: 3 }}>
            <CorrespondentCheckbox
              label="MLPA signed"
              checked={draft.mlpaSigned}
              onChange={(on) => {
                set("mlpaSigned", on);
                if (on) toggleAgreement(AGREEMENT_MLPA, true);
              }}
            />
            {OTHER_AGREEMENTS.map((a) => (
              <CorrespondentCheckbox
                key={a}
                label={a}
                checked={agreements.includes(a)}
                disabled={savedAgreements === null}
                onChange={(on) => toggleAgreement(a, on)}
              />
            ))}
          </Flx>
          {/* Two columns: the signing facts on the left, the executed file on
              the right, split by a hairline (stacks on small screens). */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "minmax(0, 1fr) 1px minmax(0, 1fr)",
              },
              gap: 3,
            }}
          >
            <Flx column g={2.5} sx={{ minWidth: 0 }}>
              <ColumnHead>MLPA</ColumnHead>
              <Grid>
                <Cell span={6} spanSm={6}>
                  <PricingField label="Signed date">
                    <TextField
                      size="small"
                      fullWidth
                      type="date"
                      value={draft.mlpaSignedDate}
                      onChange={(e) => set("mlpaSignedDate", e.target.value)}
                      sx={FIELD_SX}
                    />
                  </PricingField>
                </Cell>
                <Cell span={6} spanSm={6}>
                  <PricingField label="Signatory title">
                    <TextField
                      size="small"
                      fullWidth
                      value={draft.mlpaSignatoryTitle}
                      onChange={(e) => set("mlpaSignatoryTitle", e.target.value)}
                      sx={FIELD_SX}
                    />
                  </PricingField>
                </Cell>
                <Cell span={12}>
                  {/* The signatory is a full contact record (mlpaSignedBy) —
                      edited here in the same panel the Contacts tab uses, so
                      nobody has to leave the Profile tab. */}
                  <PricingField label="Signatory">
                    <Flx
                      ac
                      jb
                      g={1}
                      sx={{
                        minHeight: 40,
                        border: `1px solid ${PT.line}`,
                        borderRadius: "6px",
                        background: "#fff",
                        pl: 1.75,
                        pr: 0.75,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: 13,
                          color: signatory ? PT.ink2 : PT.faint,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {signatoryLabel || "No signatory set"}
                      </Box>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setEditingSignatory(true)}
                        sx={{ flexShrink: 0 }}
                      >
                        {signatory ? "Edit" : "Set"}
                      </Button>
                    </Flx>
                  </PricingField>
                </Cell>
              </Grid>
            </Flx>

            <Box sx={{ display: { xs: "none", md: "block" }, background: slate[200] }} />

            <Box sx={{ minWidth: 0 }}>
              <ColumnHead>Executed agreement</ColumnHead>
              {/* Uploads the executed MLPA as a new version (stage "Final
                  Executed", superseding the one shown). Promoting it to the
                  agreement of record stays on the Agreements tab. */}
              <FileSlot
                doc={mlpaDoc}
                fallbackName="MLPA"
                uploading={mlpaUpload.uploading}
                error={mlpaUpload.error}
                onFile={uploadMlpa}
                onPreview={setPreviewDoc}
                extra={
                  <Flx ac g={0.75} sx={{ flexShrink: 0 }}>
                    {mlpaDoc?.agreementVersion ? (
                      <SoftBadge tone="slate">v{mlpaDoc.agreementVersion}</SoftBadge>
                    ) : null}
                    {mlpaDoc ? <StatusChip status={mlpaDoc.status} /> : null}
                  </Flx>
                }
              />
              <Box sx={{ mt: 1, fontSize: 12.5, color: slate[500] }}>
                Versions are reviewed and promoted on the{" "}
                {onOpenAgreements ? (
                  <Box
                    component="button"
                    type="button"
                    onClick={onOpenAgreements}
                    sx={{
                      border: "none",
                      background: "none",
                      p: 0,
                      font: "inherit",
                      color: teal[700],
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Agreements tab
                  </Box>
                ) : (
                  "Agreements tab"
                )}
                .
              </Box>
            </Box>
          </Box>
        </ProfileSection>

      </PricingPageLayout>

      {editingSignatory && (
        <CorrespondentContactPanel
          title="MLPA signatory"
          subtitle={detail?.companyName}
          initial={signatory}
          mode="object"
          onSave={saveSignatory}
          onClose={() => setEditingSignatory(false)}
        />
      )}

      <AgreementDocumentModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        doc={previewDoc}
        companyId={detail?._id ?? detail?.oid}
        versions={mlpaVersions}
      />

      <PendingChangesBar
        dirty={dirty}
        saving={saving}
        onSave={save}
        onDiscard={() => {
          setDraft(buildDraft());
          setAgreements(savedAgreements ?? []);
        }}
        label="Save profile"
        subText={
          missingReason ? "Add a reason to mark this partner Inactive" : undefined
        }
      />
    </>
  );
}
